import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faTimes,
    faPlane,
    faClock,
    faUser,
    faMapMarkerAlt,
    faLink,
    faTowerCell, faCodeBranch
} from '@fortawesome/free-solid-svg-icons';
import { Role } from '../../components/auth-context/role';
import api from '../../api/api';
import './flight-manager.scss';

export function FlightManager() {
    const [flights, setFlights] = useState([]);
    const [error, setError] = useState(null);
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const fetchPendingFlights = async () => {
            try {
                const response = await api.get('/flights/pending');
                if (response?.data) {
                    const filteredFlights = response.data.filter(
                        (flight) => flight.startFlight && flight.closeFlight
                    );
                    setFlights(filteredFlights);
                    setError(null);
                } else {
                    throw new Error('No flight data received from server');
                }
            } catch (error) {
                console.error('Failed to fetch pending flights:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                });
                const errorMessage =
                    error.response?.data?.error ||
                    error.message ||
                    'Failed to fetch pending flights';
                setError(errorMessage);
            }
        };
        fetchPendingFlights();
    }, []);

    const openValidationModal = (flight) => {
        setSelectedFlight(flight);
        console.log(flight)
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedFlight(null);
    };

    const calculateFlightDuration = (startTime, endTime) => {
        const start = new Date(startTime);
        const end = new Date(endTime);
        const durationMs = end - start;
        const hours = Math.floor(durationMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    };

    const handleStatusChange = async (status) => {
        if (!selectedFlight) return;
        
        try {
            await api.put(`/flights/${selectedFlight.id}/status`, { status, comment: '' });
            setFlights(flights.filter((flight) => flight.id !== selectedFlight.id));
            closeModal();
            // No mostrar alerta cuando es exitoso
        } catch (error) {
            console.error(`Failed to ${status === 2 ? 'accept' : 'reject'} flight:`, {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            });
            const errorMessage =
                error.response?.data?.error ||
                error.message ||
                `Error al ${status === 2 ? 'validar' : 'rechazar'} el vuelo`;
            setError(errorMessage);
            window.alert(errorMessage);
        }
    };


    return (
        <div className="view-model">
            <h2>Flight Manager</h2>
            <Role has="VALIDATOR_MANAGER">
                {error && <p className="error-message">{error}</p>}
                <div className="table-container">
                    <table className="pilot-table">
                        <thead>
                        <tr>
                            <th>#ID</th>
                            <th>Pilot</th>
                            <th>Aircraft</th>
                            <th>Departure</th>
                            <th>Arrival</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                            <th>Type</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {flights.length === 0 ? (
                            <tr className="background-change">
                                <td colSpan="9">No pending flights found.</td>
                            </tr>
                        ) : (
                            flights.map((flight) => (
                                <tr key={flight.id} className="background-change">
                                    <td>{flight.id}</td>
                                    <td>{`${flight.pilot.firstName} (${flight.pilot.callsign || 'N/A'})`}</td>
                                    <td>{flight.aircraft}</td>
                                    <td>{`${flight.departure.icao} (${flight.departure.name})`}</td>
                                    <td>{`${flight.arrival.icao} (${flight.arrival.name})`}</td>
                                    <td>{new Date(flight.startFlight).toLocaleString()}</td>
                                    <td>{new Date(flight.closeFlight).toLocaleString()}</td>
                                    <td>
                                        {flight.type === 1
                                            ? 'Manual'
                                            : flight.type === 2
                                                ? 'Regular'
                                                : flight.type === 3
                                                    ? 'Charter'
                                                    : flight.type === 4
                                                        ? 'ACARS'
                                                        : 'Free Mode'}
                                    </td>
                                    <td>
                                        <button
                                            className="btn"
                                            onClick={() => openValidationModal(flight)}
                                            title="Review flight"
                                        >
                                            <FontAwesomeIcon icon={faCheck}/> Revisar
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </Role>
            
            {/* Modal de validación */}
            {showModal && selectedFlight && (
                <>
                    <div className="modal-overlay" onClick={closeModal}></div>
                    <div className="modal-content flight-validation-modal">
                        <div className="modal-header">
                            <h2>Flight Validation</h2>
                            <button className="modal-close" onClick={closeModal}>×</button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="flight-summary">
                                <h3><FontAwesomeIcon icon={faPlane}/> Flight Summary #{selectedFlight.id}</h3>

                                <div className="flight-detail-row">
                                    <FontAwesomeIcon icon={faUser} className="detail-icon"/>
                                    <strong>Pilot:</strong>
                                    <span>{selectedFlight.pilot.firstName} {selectedFlight.pilot.lastName} ({selectedFlight.pilot.callsign})</span>
                                </div>

                                <div className="flight-detail-row">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="detail-icon"/>
                                    <strong>Rute:</strong>
                                    <span>{selectedFlight.departure.icao} → {selectedFlight.arrival.icao}</span>
                                </div>

                                <div className="flight-detail-row">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="detail-icon"/>
                                    <strong>Departure:</strong>
                                    <span>{selectedFlight.departure.name}</span>
                                </div>

                                <div className="flight-detail-row">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="detail-icon"/>
                                    <strong>Arrival:</strong>
                                    <span>{selectedFlight.arrival.name}</span>
                                </div>

                                <div className="flight-detail-row">
                                    <FontAwesomeIcon icon={faClock} className="detail-icon"/>
                                    <strong>Duration:</strong>
                                    <span className="duration-highlight">
                                        {calculateFlightDuration(selectedFlight.startFlight, selectedFlight.closeFlight)}
                                    </span>
                                </div>

                                <div className="flight-detail-row">
                                    <FontAwesomeIcon icon={faPlane} className="detail-icon"/>
                                    <strong>Aircraft:</strong>
                                    <span>{selectedFlight.aircraft}</span>
                                </div>

                                <div className="flight-detail-row">
                                    <FontAwesomeIcon icon={faCodeBranch}  className="detail-icon"/>
                                    <strong>Flight type:</strong>
                                    <span>
                                        {selectedFlight.type === 1
                                            ? 'Manual'
                                            : selectedFlight.type === 2
                                                ? 'Regular'
                                                : selectedFlight.type === 3
                                                    ? 'Charter'
                                                    : selectedFlight.type === 4
                                                        ? 'ACARS'
                                                        : 'Free Mode'}
                                    </span>
                                </div>
                                {selectedFlight.type === 1 && (
                                    <div className="flight-detail-row">
                                        <FontAwesomeIcon icon={faLink} className="detail-icon"/>
                                        <strong>Tracker Link:</strong>
                                        <span><a href={selectedFlight.pirep} target={"_blank"}>Link</a></span>
                                    </div>
                                )}
                                <div className="flight-detail-row">
                                    <FontAwesomeIcon icon={faTowerCell} className="detail-icon"/>
                                    <strong>Network:</strong>
                                    <span>{selectedFlight.network}</span>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn"
                                onClick={() => handleStatusChange(2)}
                            >
                                <FontAwesomeIcon icon={faCheck} /> Valid Flight
                            </button>
                            <button 
                                className="btn danger" 
                                onClick={() => handleStatusChange(3)}
                            >
                                <FontAwesomeIcon icon={faTimes} /> Reject Flight
                            </button>
                            <button className="btn secondary" onClick={closeModal}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}